import {
  BarcodeConfigApi,
  DocumentConfigApi,
  PointsConfigApi,
  PriceConfigApi,
  RoundingConfigApi,
  SaleCodeConfigApi,
  ServiceChargeConfigApi,
  StoreConfigData,
  WeightBarcodeConfigApi,
} from 'types/settings/settingsApiTypes';
import {
  BarcodeBrand,
  BarcodeLayoutType,
  BarcodeSettings,
  BarcodeSize,
  DocumentSettings,
  InvoiceCodeSettings,
  LoyaltyPointsSettings,
  ProductPriceSettings,
  RoundingCondition,
  RoundingSettings,
  SaleCodeFormat,
  SaleCodeType,
  SaleCodeYearType,
  ServiceChargeSettings,
  SettingsFormValues,
  WeightBarcodeSettings,
} from 'pages/Settings/settingsTypes';

const GROUP_PRICE_SLOTS = 5; // group_price[0..4]
const DEFAULT_ROUNDING_INTERVAL = 0.5;

// ---------- price ----------
export const apiToPrice = (api: PriceConfigApi): ProductPriceSettings => {
  const groupPriceNames = Array.from({ length: GROUP_PRICE_SLOTS }, () => '');
  (api.group_price || []).forEach((item) => {
    if (item.id >= 0 && item.id < GROUP_PRICE_SLOTS) {
      groupPriceNames[item.id] = item.name ?? '';
    }
  });
  return { groupPriceNames, usePrice: true, usePriceSale: true };
};

export const priceToApi = (form: ProductPriceSettings): PriceConfigApi => ({
  // ส่งครบทั้ง 5 ช่อง — backend จะตัดช่องที่ชื่อว่างทิ้งเอง
  group_price: form.groupPriceNames.map((name, id) => ({
    id,
    name: name.trim(),
  })),
});

// ---------- barcode ----------
export const apiToBarcode = (api: BarcodeConfigApi): BarcodeSettings => ({
  idType: api.bracode_id_type === 2 ? 'barcode' : 'product_code',
  brand: (api.bracode_brand || 'see_dlabel') as BarcodeBrand,
  size: (api.bracode_size || '') as BarcodeSize,
  layoutType: (api.bracode_type || 1) as BarcodeLayoutType,
  customItemCode: Boolean(api.custom_item_code),
});

export const barcodeToApi = (form: BarcodeSettings): BarcodeConfigApi => ({
  bracode_id_type: form.idType === 'barcode' ? 2 : 1,
  bracode_brand: form.brand,
  bracode_type: form.layoutType,
  bracode_size: form.size,
  custom_item_code: form.customItemCode,
});

// ---------- weight barcode ----------
export const apiToWeightBarcode = (
  api: WeightBarcodeConfigApi
): WeightBarcodeSettings => ({
  enabled: Boolean(api.enabled),
  barcodeType: api.barcode_type || 'weight',
  prefix: api.prefix || '20',
  prefixLength: api.prefix_length || 2,
  pluDigits: api.plu_digits || 5,
  weightDigits: api.value_digits ?? api.weight_digits ?? 5,
  checkLength: api.check_length ?? 1,
  checkType: api.check_type || 'ean13',
  valueDecimals:
    api.value_decimals ?? ((api.barcode_type || 'weight') === 'weight' ? 3 : 2),
  totalLength: api.total_length ?? null,
  formatMask: api.format_mask || '',
});

export const weightBarcodeToApi = (
  form: WeightBarcodeSettings
): WeightBarcodeConfigApi => {
  const checkLength = 1;
  const checkType = 'ean13';
  const valueDecimals = form.barcodeType === 'weight' ? 3 : 2;
  const totalLength =
    form.prefixLength + form.pluDigits + form.weightDigits + checkLength;

  return {
    enabled: form.enabled,
    barcode_type: form.barcodeType,
    prefix: form.prefix,
    prefix_length: form.prefixLength,
    plu_digits: form.pluDigits,
    value_digits: form.weightDigits,
    weight_digits: form.weightDigits,
    check_length: checkLength,
    check_type: checkType,
    value_decimals: valueDecimals,
    total_length: totalLength,
    format_mask: form.formatMask || null,
  };
};

// ---------- document ----------
export const apiToDocument = (api: DocumentConfigApi): DocumentSettings => ({
  companyLogoUrl: api.logo_company ?? '',
  receiptLogoUrl: api.logo_thermal ?? '',
  templateType: api.doc_template === 1 ? 'old' : 'new',
  receiptComment: api.doc_comment ?? '',
  quotationComment: api.quotation_comment ?? '',
});

export const documentToApi = (form: DocumentSettings): DocumentConfigApi => ({
  // logo_*: ส่งค่าใน form กลับไป — backend แยกแยะ full URL (ไม่เปลี่ยน) /
  // temp file_name (ย้ายเป็นถาวร) / "" (ลบ) ให้เอง
  logo_company: form.companyLogoUrl,
  logo_thermal: form.receiptLogoUrl,
  doc_template: form.templateType === 'old' ? 1 : 0,
  doc_comment: form.receiptComment,
  quotation_comment: form.quotationComment,
});

// ---------- rounding ----------
export const apiToRounding = (api: RoundingConfigApi): RoundingSettings => {
  const condition = (api.rounding_bill_condition || 0) as RoundingCondition;
  return {
    condition,
    interval:
      condition === 0
        ? null
        : api.rounding_bill_interval || DEFAULT_ROUNDING_INTERVAL,
  };
};

export const roundingToApi = (form: RoundingSettings): RoundingConfigApi => ({
  rounding_bill_condition: form.condition,
  rounding_bill_interval: form.condition === 0 ? 0 : (form.interval ?? 0),
});

// ---------- points ----------
export const apiToPoints = (api: PointsConfigApi): LoyaltyPointsSettings => ({
  isActive: Boolean(api.point_is_activate),
  expiryYear: api.expiry_year || 1,
});

export const pointsToApi = (form: LoyaltyPointsSettings): PointsConfigApi => ({
  point_is_activate: form.isActive,
  expiry_year: form.expiryYear,
});

// ---------- service charge ----------
export const apiToServiceCharge = (
  api: ServiceChargeConfigApi
): ServiceChargeSettings => ({
  isActive: Boolean(api.service_charge_is_active),
  percent: api.service_charge_percent ?? 0,
  vat: api.service_charge_vat ?? 0,
});

export const serviceChargeToApi = (
  form: ServiceChargeSettings
): ServiceChargeConfigApi => ({
  service_charge_is_active: form.isActive,
  service_charge_percent: form.percent,
  service_charge_vat: form.vat,
});

// ---------- invoice code ----------
export const apiToInvoiceCode = (
  api: SaleCodeConfigApi
): InvoiceCodeSettings => ({
  prefix: api.prefix ?? '',
  type: (api.type || 0) as SaleCodeType,
  typeYear: (api.type_year || 0) as SaleCodeYearType,
  format: (api.format || '') as SaleCodeFormat,
  num: api.num || 0,
});

export const invoiceCodeToApi = (
  form: InvoiceCodeSettings
): SaleCodeConfigApi => ({
  prefix: form.prefix,
  type: form.type,
  type_year: form.typeYear,
  format: form.format,
  num: form.num,
});

// ---------- รวมทั้งหมด (ตอน GET) ----------
export const apiToForm = (data: StoreConfigData): SettingsFormValues => ({
  productPrice: apiToPrice(data.price),
  barcode: apiToBarcode(data.barcode),
  weightBarcode: apiToWeightBarcode(data.weight_barcode),
  document: apiToDocument(data.document),
  rounding: apiToRounding(data.rounding),
  loyaltyPoints: apiToPoints(data.points),
  serviceCharge: apiToServiceCharge(data.service_charge),
  invoiceCode: apiToInvoiceCode(data.invoice_code),
});
